import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
  FiX, FiSend, FiPlus, FiSearch, FiPhone, FiEdit2, FiTrash2,
  FiCheck, FiLoader, FiAlertCircle, FiArrowLeft, FiArrowRight,
  FiMessageSquare, FiUsers, FiHash
} from 'react-icons/fi'

interface Message {
  id: string
  content: string | null
  message_type: string
  timestamp: string
  from_me?: number | boolean
  fromMe?: boolean
}

interface InternalContact {
  id: string
  name: string
  phone: string
  category: string | null
}

interface ForwardModalProps {
  isOpen: boolean
  onClose: () => void
  selectedMessages: Message[]
  sourceContactName?: string | null
  onSuccess?: (destinationConversationId?: string | null) => void
}

function formatPhone(p: string): string {
  const d = p.replace(/\D/g, '')
  if (d.length === 13 && d.startsWith('55')) return `(${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`
  if (d.length === 12 && d.startsWith('55')) return `(${d.slice(2, 4)}) ${d.slice(4, 8)}-${d.slice(8)}`
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return p
}

function formatTime(ts: string): string {
  return new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Campo_Grande' })
}

// Persiste o último destino usado pra pré-selecionar no próximo encaminhamento.
// Reduz cliques pro caso comum "operador sempre manda pro mesmo depósito".
const LAST_DEST_KEY = 'forward_last_destination_v1'
type LastDest = { type: 'saved'; contactId: string } | { type: 'manual'; phone: string }
function loadLastDestination(): LastDest | null {
  try {
    const raw = localStorage.getItem(LAST_DEST_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed?.type === 'saved' && typeof parsed.contactId === 'string') return parsed
    if (parsed?.type === 'manual' && typeof parsed.phone === 'string') return parsed
    return null
  } catch { return null }
}
function saveLastDestination(dest: LastDest) {
  try { localStorage.setItem(LAST_DEST_KEY, JSON.stringify(dest)) } catch { /* ignore */ }
}

// Cor consistente derivada do nome do contato — mesma inicial = mesma cor.
const avatarColors = ['bg-blue-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-purple-500', 'bg-sky-500', 'bg-orange-500', 'bg-teal-500']
function colorOf(name: string): string {
  let h = 0
  for (let i = 0; i < name.length; i++) h = ((h << 5) - h) + name.charCodeAt(i)
  return avatarColors[Math.abs(h) % avatarColors.length]
}

export function ForwardModal({ isOpen, onClose, selectedMessages, sourceContactName, onSuccess }: ForwardModalProps) {
  const { authenticatedFetch } = useAuth()
  const [step, setStep] = useState<1 | 2>(1)
  const [contacts, setContacts] = useState<InternalContact[]>([])
  const [loadingContacts, setLoadingContacts] = useState(false)
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null)
  const [destinationTab, setDestinationTab] = useState<'saved' | 'manual'>('saved')
  const [manualPhone, setManualPhone] = useState('')
  const [instruction, setInstruction] = useState('')
  const [search, setSearch] = useState('')
  // Cadastro/edição inline. `editingContactId=null` quando é novo cadastro.
  const [showAddNew, setShowAddNew] = useState(false)
  const [editingContactId, setEditingContactId] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [newPhone, setNewPhone] = useState('')
  const [newCategory, setNewCategory] = useState('')
  const [creating, setCreating] = useState(false)
  // Envio
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setLoadingContacts(true)
    authenticatedFetch('/api/internal-contacts')
      .then(r => r.ok ? r.json() : { contacts: [] })
      .then(d => {
        if (cancelled) return
        const list: InternalContact[] = d.contacts || []
        setContacts(list)
        // Restaura último destino — só DEPOIS dos contatos carregarem (precisa
        // validar que o id ainda existe no caso de 'saved').
        const last = loadLastDestination()
        if (last) {
          if (last.type === 'saved' && list.some(c => c.id === last.contactId)) {
            setSelectedContactId(last.contactId)
            setDestinationTab('saved')
          } else if (last.type === 'manual') {
            setManualPhone(last.phone)
            setDestinationTab('manual')
          }
        }
      })
      .catch(() => { if (!cancelled) setContacts([]) })
      .finally(() => { if (!cancelled) setLoadingContacts(false) })
    return () => { cancelled = true }
  }, [isOpen, authenticatedFetch])

  // Reset state quando abre/fecha
  useEffect(() => {
    if (isOpen) {
      setStep(1)
      setSelectedContactId(null); setManualPhone(''); setInstruction('')
      setShowAddNew(false); setEditingContactId(null); setNewName(''); setNewPhone(''); setNewCategory('')
      setError(null); setSuccess(false); setSearch('')
      setDestinationTab('saved')
    }
  }, [isOpen])

  // Abre o form pré-preenchido com dados do contato (modo edição).
  const startEditContact = (c: InternalContact) => {
    setEditingContactId(c.id)
    setNewName(c.name)
    setNewPhone(c.phone)
    setNewCategory(c.category || '')
    setShowAddNew(true)
    setError(null)
  }

  const cancelEdit = () => {
    setEditingContactId(null)
    setShowAddNew(false)
    setNewName(''); setNewPhone(''); setNewCategory('')
    setError(null)
  }

  const handleDeleteContact = async (c: InternalContact) => {
    if (!confirm(`Excluir o contato "${c.name}"?`)) return
    try {
      await authenticatedFetch(`/api/internal-contacts/${c.id}`, { method: 'DELETE' })
      setContacts(prev => prev.filter(x => x.id !== c.id))
      if (selectedContactId === c.id) setSelectedContactId(null)
      if (editingContactId === c.id) cancelEdit()
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir')
    }
  }

  const filteredContacts = useMemo(() => {
    if (!search.trim()) return contacts
    const t = search.toLowerCase()
    return contacts.filter(c =>
      c.name.toLowerCase().includes(t) ||
      c.phone.includes(t) ||
      (c.category || '').toLowerCase().includes(t)
    )
  }, [contacts, search])

  const groupedContacts = useMemo(() => {
    const groups: Record<string, InternalContact[]> = {}
    for (const c of filteredContacts) {
      const k = c.category?.trim() || 'Outros'
      if (!groups[k]) groups[k] = []
      groups[k].push(c)
    }
    return groups
  }, [filteredContacts])

  if (!isOpen) return null

  // Validações
  const manualPhoneDigits = manualPhone.replace(/\D/g, '')
  const manualPhoneValid = manualPhoneDigits.length === 10 || manualPhoneDigits.length === 11
    || (manualPhoneDigits.length === 12 && manualPhoneDigits.startsWith('55'))
    || (manualPhoneDigits.length === 13 && manualPhoneDigits.startsWith('55'))
  const destinationReady = destinationTab === 'saved'
    ? !!selectedContactId
    : manualPhoneValid

  const canContinue = selectedMessages.length > 0
  const canSend = !sending && destinationReady && selectedMessages.length > 0

  // Save unificado: PATCH se editando, POST se novo.
  const handleSaveContact = async () => {
    if (!newName.trim() || !newPhone.trim()) {
      setError('Nome e telefone são obrigatórios')
      return
    }
    setCreating(true); setError(null)
    try {
      const body = { name: newName, phone: newPhone, category: newCategory || null }
      const r = editingContactId
        ? await authenticatedFetch(`/api/internal-contacts/${editingContactId}`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          })
        : await authenticatedFetch('/api/internal-contacts', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || 'Erro ao salvar')
      const updated = data.contact
      setContacts(prev => {
        const filtered = prev.filter(x => x.id !== updated.id)
        return [...filtered, updated].sort((a, b) =>
          (a.category || 'zzz').localeCompare(b.category || 'zzz') || a.name.localeCompare(b.name)
        )
      })
      setSelectedContactId(updated.id)
      setShowAddNew(false); setEditingContactId(null)
      setNewName(''); setNewPhone(''); setNewCategory('')
    } catch (err: any) {
      setError(err.message)
    } finally {
      setCreating(false)
    }
  }

  const handleSend = async () => {
    setSending(true); setError(null)
    try {
      const body: any = {
        messageIds: selectedMessages.map(m => m.id),
        instruction: instruction.trim() || undefined,
      }
      if (destinationTab === 'saved' && selectedContactId) body.internalContactId = selectedContactId
      else body.destinationPhone = manualPhone

      const r = await authenticatedFetch('/api/whatsapp-messages/forward', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || 'Erro ao encaminhar')
      setSuccess(true)
      // Persiste o destino pra pré-selecionar no próximo open do modal.
      if (destinationTab === 'saved' && selectedContactId) {
        saveLastDestination({ type: 'saved', contactId: selectedContactId })
      } else if (destinationTab === 'manual' && manualPhone.trim()) {
        saveLastDestination({ type: 'manual', phone: manualPhone.trim() })
      }
      setTimeout(() => {
        onSuccess?.(data.destinationConversationId)
        onClose()
      }, 1200)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSending(false)
    }
  }

  const selectedContact = selectedContactId
    ? contacts.find(c => c.id === selectedContactId)
    : null
  const sourceInitial = (sourceContactName || '?').trim().charAt(0).toUpperCase()
  const destInitial = selectedContact
    ? selectedContact.name.charAt(0).toUpperCase()
    : (destinationTab === 'manual' && manualPhoneValid ? '?' : null)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 glass-overlay">
      <div className="glass-panel rounded-xl max-w-lg w-full mx-auto flex flex-col max-h-[92vh]">

        {/* Header com indicador de passo */}
        <div className="px-4 py-3 border-b border-border flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
              <FiSend className="w-4 h-4 text-sky-500" />
              Encaminhar mensagens
            </h3>
            <button onClick={onClose} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground" title="Fechar (Esc)">
              <FiX className="w-4 h-4" />
            </button>
          </div>
          {/* Stepper visual */}
          <div className="flex items-center gap-1.5">
            <div className={`flex items-center gap-1.5 ${step === 1 ? 'text-sky-600 dark:text-sky-400' : 'text-muted-foreground'}`}>
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step === 1 ? 'bg-sky-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                {step > 1 ? <FiCheck className="w-3 h-3" /> : '1'}
              </div>
              <span className="text-[11px] font-medium">Revisar</span>
            </div>
            <div className={`flex-1 h-px ${step === 2 ? 'bg-sky-500' : 'bg-border'}`} />
            <div className={`flex items-center gap-1.5 ${step === 2 ? 'text-sky-600 dark:text-sky-400' : 'text-muted-foreground'}`}>
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step === 2 ? 'bg-sky-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                2
              </div>
              <span className="text-[11px] font-medium">Destino</span>
            </div>
          </div>
        </div>

        {/* Body — passo 1 ou 2 */}
        <div className="flex-1 overflow-y-auto">
          {step === 1 && (
            <div className="p-4 space-y-4">
              {/* "De" — quem mandou as msgs originais */}
              {sourceContactName && (
                <div className="flex items-center gap-2.5 px-3 py-2 bg-muted/30 border border-border rounded-lg">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold ${colorOf(sourceContactName)}`}>
                    {sourceInitial}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Mensagens de</div>
                    <div className="text-sm font-semibold text-foreground truncate">{sourceContactName}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Total</div>
                    <div className="text-sm font-bold text-foreground">{selectedMessages.length} msg{selectedMessages.length === 1 ? '' : 's'}</div>
                  </div>
                </div>
              )}

              {/* Preview das mensagens — bubble compacta com lado indicado */}
              <div>
                <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                  <FiMessageSquare className="w-3 h-3" />
                  Mensagens selecionadas
                </div>
                <div className="space-y-1 bg-muted/20 border border-border rounded-lg p-2 max-h-48 overflow-y-auto">
                  {selectedMessages.map((m) => {
                    const isFromMe = m.fromMe === true || Number(m.from_me) === 1
                    return (
                      <div key={m.id} className={`flex ${isFromMe ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] px-2.5 py-1.5 rounded-lg text-[11px] ${
                          isFromMe
                            ? 'bg-emerald-500/15 text-foreground rounded-br-sm'
                            : 'bg-card text-foreground border border-border rounded-bl-sm'
                        }`}>
                          <div className="break-words">
                            {m.content && m.content.length > 200 ? m.content.slice(0, 200) + '…' : (m.content || `[${m.message_type}]`)}
                          </div>
                          <div className={`text-[9px] mt-0.5 ${isFromMe ? 'text-muted-foreground' : 'text-muted-foreground/70'}`}>
                            {formatTime(m.timestamp)}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Instrução do operador */}
              <div>
                <label className="block text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                  Sua mensagem <span className="text-muted-foreground/60 normal-case font-normal">(opcional)</span>
                </label>
                <textarea
                  value={instruction}
                  onChange={e => setInstruction(e.target.value)}
                  placeholder="Ex: cliente perguntando da caixa dela, pode dar um retorno?"
                  rows={3}
                  className="w-full px-3 py-2 text-xs bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-sky-500 resize-none placeholder:text-muted-foreground/60"
                />
                <div className="text-[10px] text-muted-foreground/60 mt-1">
                  Aparece no topo da mensagem encaminhada
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="p-4 space-y-3">
              {/* Cabeçalho do destino: De → Para */}
              <div className="flex items-center gap-2">
                <div className="flex-1 flex items-center gap-2 px-2.5 py-1.5 bg-muted/30 border border-border rounded-lg min-w-0">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-semibold shrink-0 ${sourceContactName ? colorOf(sourceContactName) : 'bg-muted-foreground/40'}`}>
                    {sourceInitial}
                  </div>
                  <span className="text-[11px] text-foreground truncate font-medium">{sourceContactName || 'Cliente'}</span>
                </div>
                <FiArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className={`flex-1 flex items-center gap-2 px-2.5 py-1.5 rounded-lg min-w-0 border ${destinationReady ? 'bg-sky-500/10 border-sky-500/40' : 'bg-muted/30 border-dashed border-border'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-semibold shrink-0 ${
                    selectedContact ? colorOf(selectedContact.name) : destinationReady ? 'bg-sky-500' : 'bg-muted-foreground/40'
                  }`}>
                    {destInitial || '?'}
                  </div>
                  <span className={`text-[11px] truncate font-medium ${destinationReady ? 'text-foreground' : 'text-muted-foreground italic'}`}>
                    {selectedContact?.name || (manualPhoneValid ? formatPhone(manualPhone) : 'Escolha um destino')}
                  </span>
                </div>
              </div>
              {destinationReady && (
                <div className="text-[10px] text-muted-foreground/70 -mt-1 pl-1 italic">
                  ↻ Pré-selecionado do último encaminhamento. Pode trocar abaixo se quiser.
                </div>
              )}

              {/* Tabs Salvos / Novo número */}
              <div className="flex items-center gap-1 bg-muted/40 p-0.5 rounded-md">
                <button
                  type="button"
                  onClick={() => setDestinationTab('saved')}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-medium transition-colors ${
                    destinationTab === 'saved' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <FiUsers className="w-3 h-3" />
                  Contatos salvos
                  {contacts.length > 0 && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">{contacts.length}</span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => { setDestinationTab('manual'); setSelectedContactId(null) }}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-medium transition-colors ${
                    destinationTab === 'manual' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <FiHash className="w-3 h-3" />
                  Novo número
                </button>
              </div>

              {/* Conteúdo da tab Salvos */}
              {destinationTab === 'saved' && (
                <>
                  <div className="flex items-center gap-1.5">
                    <div className="relative flex-1">
                      <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                      <input
                        type="text"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Buscar contato..."
                        className="w-full pl-7 pr-2 py-1.5 text-[11px] bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-sky-500"
                      />
                    </div>
                    <button
                      onClick={() => setShowAddNew(v => !v)}
                      className="flex items-center gap-1 px-2 py-1.5 text-[11px] font-medium border border-border rounded-md hover:bg-secondary transition-colors"
                      title="Cadastrar novo contato"
                    >
                      <FiPlus className="w-3 h-3" /> Novo
                    </button>
                  </div>

                  {showAddNew && (
                    <div className="p-2.5 bg-sky-500/5 border border-sky-500/30 rounded-md space-y-1.5">
                      <div className="text-[10px] font-semibold text-sky-700 dark:text-sky-300 flex items-center gap-1 mb-0.5">
                        {editingContactId ? <FiEdit2 className="w-2.5 h-2.5" /> : <FiPlus className="w-2.5 h-2.5" />}
                        {editingContactId ? 'Editar contato' : 'Novo contato'}
                      </div>
                      <input
                        type="text"
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        placeholder="Nome (ex: Depósito)"
                        className="w-full px-2 py-1 text-[11px] bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-sky-500"
                      />
                      <input
                        type="text"
                        value={newPhone}
                        onChange={e => setNewPhone(e.target.value)}
                        placeholder="Telefone (DDD+número)"
                        className="w-full px-2 py-1 text-[11px] bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-sky-500"
                      />
                      <input
                        type="text"
                        value={newCategory}
                        onChange={e => setNewCategory(e.target.value)}
                        placeholder="Categoria (Depósito, Financeiro…)"
                        className="w-full px-2 py-1 text-[11px] bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-sky-500"
                      />
                      <div className="flex justify-end gap-1.5">
                        <button onClick={cancelEdit} className="px-2 py-1 text-[10px] text-muted-foreground hover:text-foreground">
                          Cancelar
                        </button>
                        <button
                          onClick={handleSaveContact}
                          disabled={creating || !newName.trim() || !newPhone.trim()}
                          className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium bg-sky-500 hover:bg-sky-600 text-white rounded transition-colors disabled:opacity-50"
                        >
                          {creating ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiCheck className="w-3 h-3" />}
                          {editingContactId ? 'Atualizar' : 'Cadastrar'}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="max-h-56 overflow-y-auto bg-background border border-border rounded-md">
                    {loadingContacts ? (
                      <div className="px-3 py-4 text-[11px] text-muted-foreground flex items-center justify-center gap-2">
                        <FiLoader className="w-3 h-3 animate-spin" /> Carregando contatos...
                      </div>
                    ) : Object.keys(groupedContacts).length === 0 ? (
                      <div className="px-3 py-6 text-center">
                        <FiUsers className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" />
                        <div className="text-[11px] text-muted-foreground">
                          {search ? 'Nenhum contato bate com a busca' : 'Nenhum contato cadastrado'}
                        </div>
                        {!search && (
                          <button onClick={() => setShowAddNew(true)} className="mt-2 text-[10px] text-sky-600 dark:text-sky-400 hover:underline">
                            + Cadastrar o primeiro
                          </button>
                        )}
                      </div>
                    ) : (
                      Object.entries(groupedContacts).map(([cat, list]) => (
                        <div key={cat}>
                          <div className="px-2.5 py-1 text-[9px] font-semibold text-muted-foreground uppercase tracking-wider bg-secondary/30 sticky top-0">
                            {cat} <span className="text-muted-foreground/50">· {list.length}</span>
                          </div>
                          {list.map(c => {
                            const isSelected = selectedContactId === c.id
                            const isEditing = editingContactId === c.id
                            return (
                              <div
                                key={c.id}
                                className={`group flex items-center gap-2.5 px-2.5 py-2 transition-colors border-l-2 ${
                                  isSelected
                                    ? 'bg-sky-500/10 border-l-sky-500'
                                    : isEditing
                                    ? 'bg-amber-500/10 border-l-amber-500'
                                    : 'border-l-transparent hover:bg-muted/40'
                                }`}
                              >
                                <button
                                  onClick={() => setSelectedContactId(c.id)}
                                  className="flex items-center gap-2.5 flex-1 min-w-0 text-left"
                                >
                                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0 ${colorOf(c.name)}`}>
                                    {c.name.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-[11px] font-semibold text-foreground truncate">{c.name}</div>
                                    <div className="text-[10px] text-muted-foreground tabular-nums">{formatPhone(c.phone)}</div>
                                  </div>
                                  {isSelected && <FiCheck className="w-4 h-4 text-sky-500 shrink-0" />}
                                </button>
                                {/* Ações de edit/delete aparecem no hover do grupo */}
                                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); startEditContact(c) }}
                                    className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-sky-600"
                                    title="Editar contato"
                                  >
                                    <FiEdit2 className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDeleteContact(c) }}
                                    className="p-1 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-500"
                                    title="Excluir contato"
                                  >
                                    <FiTrash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}

              {/* Conteúdo da tab Manual */}
              {destinationTab === 'manual' && (
                <div className="space-y-2">
                  <label className="block text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                    Digite o número
                  </label>
                  <div className="relative">
                    <FiPhone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <input
                      type="text"
                      value={manualPhone}
                      onChange={e => setManualPhone(e.target.value)}
                      placeholder="67 99638-3280 ou 67 3368-6940"
                      autoFocus
                      className="w-full pl-8 pr-2 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-sky-500"
                    />
                  </div>
                  {manualPhone.trim() && (
                    manualPhoneValid ? (
                      <div className="flex items-center gap-1.5 text-[11px] text-emerald-600">
                        <FiCheck className="w-3 h-3" />
                        {manualPhoneDigits.length === 10 || (manualPhoneDigits.length === 12 && manualPhoneDigits.startsWith('55')) ? 'Telefone fixo' : 'Celular'} válido
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-[11px] text-amber-500">
                        <FiAlertCircle className="w-3 h-3" />
                        {manualPhoneDigits.length < 10
                          ? `só ${manualPhoneDigits.length} dígitos — mínimo 10 (DDD + 8)`
                          : `formato não reconhecido (${manualPhoneDigits.length} dígitos)`}
                      </div>
                    )
                  )}
                  <div className="text-[10px] text-muted-foreground/60 italic">
                    Aceita celular (DDD + 9 + 8 dígitos) ou fixo (DDD + 8 dígitos)
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer com erro/sucesso + navegação */}
        {(error || success) && (
          <div className="flex-shrink-0 px-4 pb-2">
            {error && (
              <div className="flex items-start gap-2 px-2.5 py-2 bg-destructive/10 border border-destructive/30 rounded-md text-[11px]">
                <FiAlertCircle className="w-3.5 h-3.5 text-destructive flex-shrink-0 mt-0.5" />
                <span className="text-destructive flex-1">{error}</span>
              </div>
            )}
            {success && (
              <div className="flex items-center gap-2 px-2.5 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-md text-[11px] text-emerald-600">
                <FiCheck className="w-3.5 h-3.5" /> Encaminhado com sucesso!
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-border flex-shrink-0">
          <button
            onClick={step === 1 ? onClose : () => setStep(1)}
            disabled={sending}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          >
            {step === 1 ? 'Cancelar' : (<><FiArrowLeft className="w-3 h-3" /> Voltar</>)}
          </button>
          {step === 1 ? (
            <button
              onClick={() => setStep(2)}
              disabled={!canContinue}
              className="flex items-center gap-1.5 px-4 py-1.5 text-[11px] font-medium bg-sky-500 hover:bg-sky-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continuar
              <FiArrowRight className="w-3 h-3" />
            </button>
          ) : (
            <button
              onClick={handleSend}
              disabled={!canSend}
              className="flex items-center gap-1.5 px-4 py-1.5 text-[11px] font-medium bg-sky-500 hover:bg-sky-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiSend className="w-3 h-3" />}
              {sending ? 'Enviando...' : `Encaminhar ${selectedMessages.length} msg${selectedMessages.length === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
